import type { BaseDocument } from '../mongo/base-document.interface';

/** Milliseconds in one day */
export const MS_PER_DAY = 86_400_000;

/** Number of days before an invitation expires */
export const INVITATION_EXPIRY_DAYS = 3;

/** Invitation expiry duration in milliseconds */
export const INVITATION_EXPIRY_DAYS_IN_MS = INVITATION_EXPIRY_DAYS * MS_PER_DAY;

/** Invitation delivery channel (the provider used to send the invitation). */
export const INVITATION_CHANNEL = {
  EMAIL: 'email',
  SMS: 'sms',
  WHATSAPP: 'whatsapp',
} as const;

export type InvitationChannel = (typeof INVITATION_CHANNEL)[keyof typeof INVITATION_CHANNEL];

/** Invitation statuses persisted in MongoDB. */
export const INVITATION_STATUS = {
  ACCEPTED: 'accepted',
  CANCELLED: 'cancelled',
  CREATED: 'created',
  EXPIRED: 'expired',
  PENDING: 'pending',
} as const;

export type InvitationStatus = (typeof INVITATION_STATUS)[keyof typeof INVITATION_STATUS];

/**
 * Valid status transitions for an invitation.
 * Accepted is a terminal state with no further transitions.
 * Cancelled and expired invitations can be resent, transitioning back to pending.
 */
export const INVITATION_STATUS_TRANSITIONS: Record<InvitationStatus, readonly InvitationStatus[]> = {
  [INVITATION_STATUS.ACCEPTED]: [],
  [INVITATION_STATUS.CANCELLED]: [INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.CREATED]: [INVITATION_STATUS.CANCELLED, INVITATION_STATUS.EXPIRED, INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.EXPIRED]: [INVITATION_STATUS.PENDING],
  [INVITATION_STATUS.PENDING]: [INVITATION_STATUS.ACCEPTED, INVITATION_STATUS.CANCELLED, INVITATION_STATUS.EXPIRED],
};

export interface Invitation extends BaseDocument {
  /** Set once at acceptance; absent on invitations accepted before this field existed (legacy marker). */
  acceptedAt?: Date | null;
  activationLink: string;
  channel: InvitationChannel;
  email: string;
  expiresAt: Date;
  lastSentAt: Date | null;
  status: InvitationStatus;
  tokenNonce: number;
  userId: string;
}

export type CreateInvitation = Omit<Invitation, 'createdAt' | 'deletedAt' | 'id' | 'updatedAt'>;

export type CreateInvitationData = CreateInvitation & { _id?: string };

export type ValidateInvitationResult =
  | { email: string; firstName: string; lastName: string; signInLink: string; status: 'valid'; tenantName: string }
  | { status: 'accepted' | 'cancelled' | 'created' | 'expired' | 'not_found' };
